// Reordering of the article list.
//
// Two rules keep this robust, and both are the opposite of what this file used to do:
//
// 1. It never moves a row in the DOM. The list is rendered by Blazor, so reordering the nodes
//    here leaves Blazor's render tree describing an order the DOM no longer has; the next diff
//    then writes article content into the wrong rows, or snaps the order back. The drop only
//    reports what happened and the server re-renders the list in the new order. The drop
//    indicator is written to a data- attribute, which Blazor never renders and therefore never
//    overwrites.
//
// 2. A drop reports a single move -- "article X goes before/after article Y" -- not the new order
//    of the rows on screen. With a filter on, the rows on screen are only a subsequence of the
//    magazine, so their order says nothing about where the hidden articles belong. A single move
//    applies unchanged to the full order and leaves every other article, shown or hidden, where
//    it was. See MagazineService.MoveArticle.
//
// Listeners are delegated from the document: the container and its rows are created and destroyed
// by Blazor whenever the magazine or the filter changes, so there is nothing stable to bind to
// except the document. Everything is gated on `dragged`, which is only set by a dragstart on one
// of our own rows, so dragging anything else over the page (a file onto an upload field, say) is
// left alone.

var dragdrop = (function () {
    var CONTAINER = '#article-container';
    var ITEM = '.article-item';

    var dotNet = null;      // the ArticleList component
    var dragged = null;     // the row being dragged
    var dropTarget = null;  // the row the indicator currently sits on
    var dropBefore = false; // indicator above (true) or below (false) dropTarget
    var bound = false;

    function closest(node, selector) {
        // The target of a drag event can be a text node, which has no closest().
        var el = node instanceof Element ? node : (node && node.parentElement);
        return el ? el.closest(selector) : null;
    }

    function articleId(row) {
        if (!row) return null;
        var id = parseInt(row.dataset.articleId, 10);
        return isNaN(id) ? null : id;
    }

    // Every row in the container except the one being dragged, in rendered order.
    function rows(container) {
        return Array.prototype.filter.call(
            container.querySelectorAll(ITEM),
            function (row) { return row !== dragged && articleId(row) !== null; });
    }

    function clearIndicator() {
        if (dropTarget) {
            delete dropTarget.dataset.drop;
            delete dropTarget.dataset.dropPage;
        }
        dropTarget = null;
    }

    // The page the dragged article ends up on, for the label on the drop line. Rows carry their
    // page in the whole magazine (data-page), so this holds with a filter on as well.
    function landingPage(row, before) {
        var from = parseInt(dragged.dataset.page, 10);
        var at = parseInt(row.dataset.page, 10);
        if (isNaN(from) || isNaN(at)) return null;

        var page = before ? at : at + 1;
        return from < page ? page - 1 : page;
    }

    function setIndicator(row, before) {
        if (dropTarget === row && dropBefore === before) return;
        clearIndicator();
        dropTarget = row;
        dropBefore = before;
        row.dataset.drop = before ? 'before' : 'after';

        var page = landingPage(row, before);
        if (page !== null) row.dataset.dropPage = (page < 10 ? '0' : '') + page;
    }

    function reset() {
        clearIndicator();
        if (dragged) delete dragged.dataset.dragging;
        dragged = null;
        document.body.classList.remove('ed-dragging'); // lets the console back in, see onDragStart
    }

    // True when the drop puts the article back exactly where it already is on screen.
    // Worth catching rather than letting the server work it out: with a filter on, "before the row
    // below me" is the same picture but not the same position in the full magazine order, because
    // hidden articles can sit in between. A drag that changes nothing visible must change nothing.
    function isNoop(container, targetRow, before) {
        var all = Array.prototype.slice.call(container.querySelectorAll(ITEM));
        var from = all.indexOf(dragged);
        var to = all.indexOf(targetRow);
        if (from < 0 || to < 0) return false;
        return before ? to === from + 1 : to === from - 1;
    }

    function onDragStart(evt) {
        // A drag whose source row was taken out of the DOM mid-flight can leave dragend unfired,
        // so the previous drag is cleared here rather than trusted to have cleared itself.
        reset();

        var row = closest(evt.target, ITEM);
        if (!row || !row.closest(CONTAINER) || articleId(row) === null) return;

        dragged = row;
        row.dataset.dragging = '';
        // the console floats over the bottom of the list; while dragging it steps aside so the
        // rows under it can be dropped on
        document.body.classList.add('ed-dragging');

        if (evt.dataTransfer) {
            evt.dataTransfer.effectAllowed = 'move';
            // Firefox refuses to start a drag unless some data is attached.
            try { evt.dataTransfer.setData('text/plain', String(articleId(row))); } catch (e) { /* ignore */ }
        }
    }

    function onDragOver(evt) {
        if (!dragged) return;

        var container = closest(evt.target, CONTAINER);
        if (!container) { clearIndicator(); return; }

        evt.preventDefault(); // marks the list as a valid drop target
        if (evt.dataTransfer) evt.dataTransfer.dropEffect = 'move';

        var row = closest(evt.target, ITEM);
        if (row === dragged) { clearIndicator(); return; }

        // Over the form of the open article, which sits right under its row: that is "after the
        // open article", not a gap to ignore.
        var form = closest(evt.target, '.ed-inline');
        if (!row && form) {
            var owner = form.previousElementSibling;
            if (owner && owner.matches(ITEM) && owner !== dragged) { setIndicator(owner, false); return; }
        }

        if (row && articleId(row) !== null) {
            // The row's own box, measured against clientY. offsetY is relative to whichever nested
            // element happens to be under the pointer -- the card, the title, the summary -- which
            // is what made the above/below decision flip at random within a single row.
            var box = row.getBoundingClientRect();
            setIndicator(row, evt.clientY < box.top + box.height / 2);
            return;
        }

        // Over the list but in the gap between rows or past the end: aim at the nearest end.
        var items = rows(container);
        if (items.length === 0) { clearIndicator(); return; }

        var first = items[0];
        var last = items[items.length - 1];
        if (evt.clientY < first.getBoundingClientRect().top) setIndicator(first, true);
        else if (evt.clientY > last.getBoundingClientRect().bottom) setIndicator(last, false);
        else clearIndicator();
    }

    function onDragLeave(evt) {
        // dragover recomputes the indicator on every move and clears it outside the container, so
        // the only case left here is the pointer leaving the window, where no dragover follows.
        if (dragged && !evt.relatedTarget) clearIndicator();
    }

    function onDrop(evt) {
        if (!dragged) return;

        var container = closest(evt.target, CONTAINER);
        if (!container) { reset(); return; }

        evt.preventDefault();
        evt.stopPropagation();

        // Read the drop position from the indicator rather than from this event: the indicator is
        // what the editor was looking at when they let go, and it was computed against the row's
        // box rather than this event's arbitrary target.
        var draggedId = articleId(dragged);
        var targetRow = dropTarget;
        var before = dropBefore;
        var noop = targetRow ? isNoop(container, targetRow, before) : true;

        reset();

        if (!dotNet || !targetRow || noop) return;

        var targetId = articleId(targetRow);
        if (draggedId === null || targetId === null || targetId === draggedId) return;

        dotNet.invokeMethodAsync('MoveArticle', draggedId, targetId, before)
            .catch(function (err) { console.error('dragdrop: move failed', err); });
    }

    function onDragEnd() {
        // Fires on a cancelled drag too (escape, drop outside the window).
        reset();
    }

    // Alt+Up / Alt+Down move the open article one row, as if it were dragged past its neighbour.
    // Same server call as a drop, so the same lock and the same handling of hidden articles apply.
    function onKeyDown(evt) {
        if (!evt.altKey || (evt.key !== 'ArrowUp' && evt.key !== 'ArrowDown') || !dotNet) return;
        // inside a question's answers the keys move the answer (Onboarding), not the open article
        if (evt.target && evt.target.closest && evt.target.closest('.ed-answers')) return;

        var container = document.querySelector(CONTAINER);
        var open = container && container.querySelector(ITEM + '.active');
        if (!open) return;

        var all = Array.prototype.slice.call(container.querySelectorAll(ITEM));
        var up = evt.key === 'ArrowUp';
        var neighbour = all[all.indexOf(open) + (up ? -1 : 1)];
        if (!neighbour) return;

        evt.preventDefault();
        dotNet.invokeMethodAsync('MoveArticle', articleId(open), articleId(neighbour), up)
            .catch(function (err) { console.error('dragdrop: move failed', err); });
    }

    return {
        // Called by ArticleList once the list is on screen. Safe to call again: it replaces the
        // component reference and leaves the single set of document listeners in place.
        init: function (component) {
            dotNet = component;
            if (bound) return;

            document.addEventListener('keydown', onKeyDown);
            document.addEventListener('dragstart', onDragStart);
            document.addEventListener('dragover', onDragOver);
            document.addEventListener('dragleave', onDragLeave);
            document.addEventListener('drop', onDrop);
            document.addEventListener('dragend', onDragEnd);
            bound = true;
        },

        dispose: function () {
            reset();
            dotNet = null;
        }
    };
})();
